/* ==============================================================================
   Fachada da API
   ==============================================================================
   Métodos tipados por rota, sobre o `ApiClient` de `@concord/core`.

   Por que uma fachada e não `api.get('/guilds')` espalhado
   =======================================================
   O caminho e a forma da resposta ficam em UM lugar. Quando `/guilds` ganha um
   campo, o tipo muda aqui e o compilador aponta cada uso — em vez de um
   `as Guild[]` em cada componente, que compila e mente.

   Os tipos são escritos à mão a partir dos schemas zod do servidor, e não
   gerados. É duplicação consciente: gerar exigiria um passo de build acoplado ao
   servidor rodando, e o OpenAPI já é gerado dos mesmos schemas — o snapshot na
   CI é o que pega divergência. Ver docs/ARCHITECTURE.md §17.
   ============================================================================== */

import { ApiClient, type TokenProvider } from '@concord/core';

/* ------------------------------------------------------------------------------
   Tipos das respostas
------------------------------------------------------------------------------ */

export interface Guild {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  ownerId: string;
  isOwner: boolean;
  createdAt: string;
  /** URL pronta para `<img src>`, ou null. O servidor ja monta o caminho. */
  iconUrl: string | null;
  bannerUrl: string | null;
}

export interface Channel {
  id: string;
  guildId: string | null;
  parentId: string | null;
  type: 'category' | 'text' | 'voice' | 'forum' | 'announcement' | 'dm' | 'group_dm';
  name: string;
  topic: string | null;
  position: number;
  e2ee: boolean;
  rateLimitSeconds: number;
  maxParticipants: number | null;
}

export interface Message {
  id: string;
  channelId: string;
  content: string | null;
  createdAt: string;
  editedAt: string | null;
  mentionUsers: string[];
  mentionRoles: string[];
  mentionEveryone: boolean;
  author: {
    id: string;
    username: string;
    displayName: string | null;
    nickname: string | null;
  } | null;
}

export interface Role {
  id: string;
  guildId: string;
  name: string;
  permissions: string;
  permissionNames: string[];
  color: number | null;
  position: number;
  hoist: boolean;
  mentionable: boolean;
  managed: boolean;
  isEveryone: boolean;
  memberCount: number;
}

export interface Member {
  userId: string;
  username: string;
  displayName: string | null;
  nickname: string | null;
  status: 'online' | 'idle' | 'dnd' | 'offline';
  customStatus: string | null;
  isBot: boolean;
  isOwner: boolean;
  joinedAt: string;
  timeoutUntil: string | null;
  roles: Array<{
    id: string;
    name: string;
    color: number | null;
    position: number;
    hoist: boolean;
    isEveryone: boolean;
  }>;
  voice: {
    channelId: string;
    channelName: string;
    selfMuted: boolean;
    selfDeafened: boolean;
    serverMuted: boolean;
    serverDeafened: boolean;
    cameraOn: boolean;
    screenCount: number;
  } | null;
}

export interface Invite {
  code: string;
  guildId: string;
  channelId: string | null;
  inviterId: string | null;
  maxUses: number | null;
  uses: number;
  grantRoles: string[];
  createdAt: string;
  expiresAt: string | null;
  url: string;
}

export interface UserSummary {
  id: string;
  username: string;
  displayName: string | null;
  status: 'online' | 'idle' | 'dnd' | 'offline';
  customStatus: string | null;
}

export interface Friendship {
  user: UserSummary;
  state: 'pending' | 'accepted' | 'blocked';
  direction: 'incoming' | 'outgoing';
  note: string | null;
  createdAt: string;
  respondedAt: string | null;
}

export interface DirectChannel {
  channelId: string;
  counterpart: UserSummary | null;
  hidden: boolean;
  muted: boolean;
  lastMessageAt: string | null;
  lastMessageId: string | null;
}

export interface GuildToken {
  id: string;
  name: string;
  keyPrefix: string;
  guildId: string | null;
  defaultChannelId: string | null;
  scopes: string[];
  maxPermissions: string;
  maxPermissionNames: string[];
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}

export interface AdminTelemetry {
  /** Trinta dias, TODOS presentes — inclusive os de valor zero. */
  daily: Array<{
    day: string;
    newUsers: number;
    messages: number;
    voiceSessions: number;
  }>;
  topGuilds: Array<{
    id: string;
    name: string;
    members: number;
    channels: number;
  }>;
  storage: {
    databaseBytes: number;
    tables: Array<{ name: string; bytes: number }>;
  };
  instance: {
    version: string;
    nodeVersion: string;
    uptimeSeconds: number;
  };
  /** `null` quando a role de leitura nao esta configurada. */
  callQuality: {
    samples: number;
    rttP50: number | null;
    rttP95: number | null;
    jitterP50: number | null;
    lossP95: number | null;
    relayShare: number | null;
  } | null;
}

export interface AdminOverview {
  users: {
    total: number;
    active30d: number;
    bots: number;
    admins: number;
    deleted: number;
  };
  guilds: { total: number };
  channels: { text: number; voice: number; direct: number };
  messages: { total: number; last24h: number };
  voice: {
    liveConnections: number;
    liveRooms: number;
    liveParticipants: number;
    sessions7d: number;
  };
  contentScanning: { status: 'disabled'; reason: string };
}

export interface AdminUser {
  id: string;
  email: string;
  username: string;
  displayName: string | null;
  status: string;
  isBot: boolean;
  instanceAdmin: boolean;
  emailVerified: boolean;
  mfaEnabled: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  deletedAt: string | null;
  /** Nulo = nao suspensa. O motivo vem junto para a lista poder mostra-lo. */
  suspendedAt: string | null;
  suspendedReason: string | null;
  suspendedUntil: string | null;
  mustChangePassword: boolean;
  guildCount: number;
  messageCount: number;
  activeSessions: number;
}

export interface UserTelemetry {
  userId: string;
  username: string;
  voiceSeconds: number;
  voiceSessions: number;
  transmissions: number;
  medianRttMs: number | null;
  medianJitterMs: number | null;
  medianPacketLossPct: number | null;
  medianOutboundKbps: number | null;
  worstPacketLossPct: number | null;
  sampleCount: number;
  telemetryAvailable: boolean;
}

export interface Transmission {
  voiceSessionId: string;
  channelId: string;
  channelName: string;
  guildId: string | null;
  guildName: string | null;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  peakParticipants: number;
  peakScreens: number;
  participants: Array<{
    userId: string;
    username: string;
    joinedAt: string;
    leftAt: string | null;
    secondsPresent: number | null;
  }>;
}

export interface ScanningInfo {
  status: 'disabled';
  implemented: false;
  surfaces: Array<{ surface: string; scannable: boolean; reason: string }>;
  extensionPoint: { hook: string; signature: string; notes: string };
  legalGate: {
    required: true;
    basis: string;
    approvals: string[];
    note: string;
  };
}

/* ------------------------------------------------------------------------------
   Fachada
------------------------------------------------------------------------------ */

export function createConcordApi(tokens: TokenProvider) {
  const client = new ApiClient({ tokens });

  return {
    client,

    /* ---- Servidores e canais ---- */
    listGuilds: () => client.get<Guild[]>('/guilds'),

    createGuild: (input: { name: string; description?: string }) =>
      client.post<Guild>('/guilds', input),

    listChannels: (guildId: string) =>
      client.get<Channel[]>(`/guilds/${guildId}/channels`),

    /**
     * Edita o servidor.
     *
     * `null` em `iconMediaId` REMOVE a imagem; ausente nao mexe. A distincao
     * existe porque sem ela nao ha como tirar um icone depois de colocar.
     */
    updateGuild: (
      guildId: string,
      input: {
        name?: string;
        description?: string | null;
        iconMediaId?: string | null;
        bannerMediaId?: string | null;
      },
    ) => client.patch<Guild>(`/guilds/${guildId}`, input),

    createChannel: (
      guildId: string,
      input: {
        name: string;
        type: 'text' | 'voice' | 'announcement' | 'category' | 'forum';
        topic?: string | null;
        parentId?: string | null;
        rateLimitSeconds?: number;
        maxParticipants?: number | null;
      },
    ) => client.post<Channel>(`/guilds/${guildId}/channels`, input),

    updateChannel: (
      channelId: string,
      input: {
        name?: string;
        topic?: string | null;
        rateLimitSeconds?: number;
        maxParticipants?: number | null;
      },
    ) => client.patch<Channel>(`/channels/${channelId}`, input),

    deleteChannel: (channelId: string) =>
      client.delete<void>(`/channels/${channelId}`),

    /* ---- Imagens ---- */

    /**
     * Envia uma imagem e devolve o id e a URL.
     *
     * O conteudo vai em base64 no corpo JSON, nao em multipart. Motivo: o
     * limite e 512 KB e o cliente sempre redimensiona antes -- multipart
     * exigiria `@fastify/multipart` no servidor por nenhum ganho a esse
     * tamanho. Ver `redimensionarImagem` abaixo.
     */
    uploadMedia: (input: {
      kind: 'avatar' | 'banner' | 'guild_icon' | 'guild_banner' | 'role_icon';
      data: string;
      guildId?: string;
    }) =>
      client.post<{
        id: string;
        kind: string;
        mime: string;
        sizeBytes: number;
        width: number | null;
        height: number | null;
        animated: boolean;
        url: string;
      }>('/media', input),

    /* ---- Mensagens fixadas ---- */
    listPins: (channelId: string) =>
      client.get<
        Array<{
          messageId: string;
          channelId: string;
          content: string | null;
          createdAt: string;
          pinnedAt: string;
          pinnedBy: string | null;
          author: { id: string; username: string; displayName: string | null } | null;
        }>
      >(`/channels/${channelId}/pins`),

    pinMessage: (channelId: string, messageId: string) =>
      client.put<void>(`/channels/${channelId}/pins/${messageId}`),

    unpinMessage: (channelId: string, messageId: string) =>
      client.delete<void>(`/channels/${channelId}/pins/${messageId}`),

    /* ---- Mensagens ---- */
    listMessages: (channelId: string, options: { before?: string; limit?: number } = {}) =>
      client.get<Message[]>(`/channels/${channelId}/messages`, {
        query: {
          ...(options.before !== undefined ? { before: options.before } : {}),
          limit: options.limit ?? 50,
        },
      }),

    sendMessage: (channelId: string, input: { content: string; id?: string }) =>
      client.post<Message>(`/channels/${channelId}/messages`, input),

    /* ---- Cargos ---- */
    listRoles: (guildId: string) => client.get<Role[]>(`/guilds/${guildId}/roles`),

    createRole: (
      guildId: string,
      input: {
        name: string;
        permissions?: string;
        color?: number | null;
        hoist?: boolean;
        mentionable?: boolean;
      },
    ) => client.post<Role>(`/guilds/${guildId}/roles`, input),

    updateRole: (
      guildId: string,
      roleId: string,
      input: Partial<{
        name: string;
        permissions: string;
        color: number | null;
        hoist: boolean;
        mentionable: boolean;
        position: number;
      }>,
    ) => client.patch<Role>(`/guilds/${guildId}/roles/${roleId}`, input),

    deleteRole: (guildId: string, roleId: string) =>
      client.delete<void>(`/guilds/${guildId}/roles/${roleId}`),

    assignRole: (guildId: string, userId: string, roleId: string) =>
      client.put<void>(`/guilds/${guildId}/members/${userId}/roles/${roleId}`),

    removeRole: (guildId: string, userId: string, roleId: string) =>
      client.delete<void>(`/guilds/${guildId}/members/${userId}/roles/${roleId}`),

    setChannelOverwrite: (
      channelId: string,
      targetId: string,
      input: { targetType: 'role' | 'member'; allow: string; deny: string },
    ) => client.put<unknown>(`/channels/${channelId}/permissions/${targetId}`, input),

    /* ---- Membros e moderação ---- */
    listMembers: (guildId: string, options: { limit?: number; after?: string } = {}) =>
      client.get<Member[]>(`/guilds/${guildId}/members`, {
        query: {
          limit: options.limit ?? 100,
          ...(options.after !== undefined ? { after: options.after } : {}),
        },
      }),

    updateMember: (
      guildId: string,
      userId: string,
      input: Partial<{
        nickname: string | null;
        serverMute: boolean;
        serverDeaf: boolean;
        timeoutUntil: string | null;
      }>,
    ) => client.patch<unknown>(`/guilds/${guildId}/members/${userId}`, input),

    setOwnNickname: (guildId: string, nickname: string | null) =>
      client.patch<{ nickname: string | null }>(`/guilds/${guildId}/members/@me`, {
        nickname,
      }),

    moveMember: (guildId: string, userId: string, channelId: string | null) =>
      client.post<{ channelId: string | null }>(
        `/guilds/${guildId}/members/${userId}/move`,
        { channelId },
      ),

    kickMember: (guildId: string, userId: string) =>
      client.delete<void>(`/guilds/${guildId}/members/${userId}`),

    banMember: (
      guildId: string,
      userId: string,
      input: { reason?: string; expiresAt?: string | null } = {},
    ) => client.put<void>(`/guilds/${guildId}/bans/${userId}`, input),

    leaveGuild: (guildId: string) =>
      client.delete<void>(`/guilds/${guildId}/members/@me`),

    /* ---- Convites ---- */
    listInvites: (guildId: string) => client.get<Invite[]>(`/guilds/${guildId}/invites`),

    createInvite: (
      guildId: string,
      input: {
        channelId?: string | null;
        maxUses?: number | null;
        expiresInSeconds?: number | null;
        grantRoles?: string[];
      } = {},
    ) => client.post<Invite>(`/guilds/${guildId}/invites`, input),

    revokeInvite: (code: string) => client.delete<void>(`/invites/${code}`),

    /**
     * Prévia de convite, SEM autenticação.
     *
     * `anonymous: true` importa: quem recebe o link precisa ver para onde ele
     * leva antes de criar conta. Mandar token aqui faria a rota exigir sessão.
     */
    previewInvite: (code: string) =>
      client.get<{
        code: string;
        guild: {
          id: string;
          name: string;
          slug: string;
          description: string | null;
          memberCount: number;
        };
        inviter: { username: string; displayName: string | null } | null;
        expiresAt: string | null;
      }>(`/invites/${code}`, { anonymous: true }),

    acceptInvite: (code: string) =>
      client.post<{ guildId: string; alreadyMember: boolean; grantedRoles: string[] }>(
        `/invites/${code}`,
      ),

    /* ---- Social ---- */
    listFriends: () => client.get<Friendship[]>('/friends'),

    addFriend: (username: string, note?: string) =>
      client.post<{ state: 'pending' | 'accepted'; autoAccepted: boolean }>('/friends', {
        username,
        ...(note !== undefined ? { note } : {}),
      }),

    acceptFriend: (userId: string) => client.post<void>(`/friends/${userId}/accept`),
    removeFriend: (userId: string) => client.delete<void>(`/friends/${userId}`),
    blockUser: (userId: string) => client.put<void>(`/friends/${userId}/block`),
    unblockUser: (userId: string) => client.delete<void>(`/friends/${userId}/block`),

    searchUsers: (query: string) =>
      client.get<UserSummary[]>('/users/search', { query: { q: query } }),

    listDirectChannels: (includeHidden = false) =>
      client.get<DirectChannel[]>('/channels/@me', {
        query: { includeHidden: includeHidden ? 'true' : 'false' },
      }),

    openDirectChannel: (userId: string) =>
      client.post<{ channelId: string; created: boolean }>('/channels/@me', { userId }),

    updateDirectChannel: (
      channelId: string,
      input: { hidden?: boolean; muted?: boolean },
    ) =>
      client.patch<{ hidden: boolean; muted: boolean }>(
        `/channels/@me/${channelId}`,
        input,
      ),

    /* ---- Integrações ---- */
    listGuildTokens: (guildId: string) =>
      client.get<GuildToken[]>(`/guilds/${guildId}/tokens`),

    createGuildToken: (
      guildId: string,
      input: {
        name: string;
        scopes: string[];
        defaultChannelId?: string | null;
        maxPermissions?: string;
        expiresInDays?: number | null;
      },
    ) =>
      client.post<GuildToken & { token: string; warning: string }>(
        `/guilds/${guildId}/tokens`,
        input,
      ),

    revokeGuildToken: (guildId: string, tokenId: string, reason?: string) =>
      client.delete<void>(`/guilds/${guildId}/tokens/${tokenId}`, {
        ...(reason !== undefined ? { query: { reason } } : {}),
      }),

    listCommands: (guildId: string) =>
      client.get<
        Array<{
          id: string;
          applicationId: string;
          guildId: string | null;
          name: string;
          description: string;
          requiredPermissions: string;
          defaultDisabled: boolean;
        }>
      >(`/guilds/${guildId}/commands`),

    deleteCommand: (guildId: string, commandId: string) =>
      client.delete<void>(`/guilds/${guildId}/commands/${commandId}`),

    /* ---- Administração da instância ---- */
    adminOverview: () => client.get<AdminOverview>('/admin/overview'),
    adminTelemetry: () => client.get<AdminTelemetry>('/admin/telemetry'),

    adminListUsers: (
      options: { limit?: number; after?: string; q?: string; includeDeleted?: boolean } = {},
    ) =>
      client.get<AdminUser[]>('/admin/users', {
        query: {
          limit: options.limit ?? 50,
          ...(options.after !== undefined ? { after: options.after } : {}),
          ...(options.q !== undefined ? { q: options.q } : {}),
          includeDeleted: options.includeDeleted ? 'true' : 'false',
        },
      }),

    adminUserTelemetry: (userId: string, days = 30) =>
      client.get<UserTelemetry>(`/admin/users/${userId}/telemetry`, {
        query: { days },
      }),

    adminTransmissions: (
      options: { limit?: number; days?: number; onlyWithScreens?: boolean } = {},
    ) =>
      client.get<Transmission[]>('/admin/transmissions', {
        query: {
          limit: options.limit ?? 25,
          days: options.days ?? 7,
          onlyWithScreens: options.onlyWithScreens ? 'true' : 'false',
        },
      }),

    adminRevokeSessions: (userId: string) =>
      client.post<{ revokedSessions: number }>(
        `/admin/users/${userId}/sessions/revoke`,
      ),

    adminDeleteUser: (userId: string, input: { hardDelete?: boolean; reason?: string } = {}) =>
      client.delete<{ deleted: boolean; hard: boolean; messagesPreserved: number }>(
        `/admin/users/${userId}`,
      ).catch(async (error: unknown) => {
        /*
         * `DELETE` com corpo não é bem suportado por todo intermediário.
         *
         * A rota aceita corpo opcional; se o `delete` sem corpo falhar por causa
         * de `hardDelete`, a chamada é refeita com corpo explícito. Preferir a
         * forma sem corpo por padrão evita proxies que descartam corpo em DELETE
         * — um modo de falha difícil de diagnosticar porque só acontece em
         * produção, atrás do proxy.
         */
        if (input.hardDelete !== true && input.reason === undefined) throw error;
        return client.request<{
          deleted: boolean;
          hard: boolean;
          messagesPreserved: number;
        }>(`/admin/users/${userId}`, { method: 'DELETE', body: input });
      }),

    adminSetInstanceAdmin: (userId: string, instanceAdmin: boolean) =>
      client.put<{ instanceAdmin: boolean }>(`/admin/users/${userId}/admin`, {
        instanceAdmin,
      }),

    adminAudit: (
      options: { limit?: number; before?: number; action?: string; minSeverity?: number } = {},
    ) =>
      client.get<
        Array<{
          id: number;
          occurredAt: string;
          actorKind: string;
          actorUserId: string | null;
          actorIp: string | null;
          action: string;
          targetType: string | null;
          targetId: string | null;
          guildId: string | null;
          severity: number;
          requestId: string | null;
        }>
      >('/admin/audit', {
        query: {
          limit: options.limit ?? 50,
          ...(options.before !== undefined ? { before: options.before } : {}),
          ...(options.action !== undefined ? { action: options.action } : {}),
          minSeverity: options.minSeverity ?? 0,
        },
      }),

    adminScanning: () => client.get<ScanningInfo>('/admin/scanning'),

    /* ---- Moderação de conta ---- */

    /**
     * Suspende uma conta. `until` ausente = indefinido.
     *
     * O motivo é obrigatório na rota: escrevê-lo é o que faz quem bloqueia
     * reconsiderar, e é o que permite revisar a decisão depois.
     */
    adminSuspendUser: (
      userId: string,
      input: { reason: string; until?: string },
    ) =>
      client.put<{
        suspendedAt: string;
        suspendedUntil: string | null;
        sessionsRevoked: number;
      }>(`/admin/users/${userId}/suspension`, input),

    adminUnsuspendUser: (userId: string) =>
      client.delete<{ suspended: false }>(`/admin/users/${userId}/suspension`),

    /**
     * Define uma senha temporária e devolve em claro UMA vez.
     *
     * Não há como recuperá-la depois — o banco guarda só o hash Argon2id. Ela
     * vale para uma entrada: a troca é exigida antes de liberar o resto.
     */
    adminResetPassword: (userId: string) =>
      client.post<{
        temporaryPassword: string;
        mustChangePassword: true;
        sessionsRevoked: number;
      }>(`/admin/users/${userId}/password-reset`),

    adminUpdateProfile: (
      userId: string,
      input: { displayName?: string | null; username?: string; email?: string },
    ) =>
      client.patch<{
        id: string;
        username: string;
        email: string;
        displayName: string | null;
      }>(`/admin/users/${userId}/profile`, input),
  };
}

/* ==============================================================================
   Redimensionamento antes do upload
============================================================================== */

/**
 * Reduz uma imagem para caber no limite do servidor e devolve base64.
 *
 * Por que no CLIENTE
 * ==================
 * Reencodar no servidor exigiria `sharp` (binario nativo, ~10 MB) ou
 * ImageMagick. Este host tem ~400 MB de memoria livre e ja compartilha espaco
 * com outra aplicacao em producao -- nao ha folga para isso. O navegador ja tem
 * um codec completo e ocioso.
 *
 * O servidor NAO confia nisto. Ele le os magic bytes, mede o buffer real e
 * limita os pixels. Este caminho e conveniencia, nao seguranca: sem ele, uma
 * foto de celular de 4 MB seria recusada com 413 e a pessoa teria de
 * redimensionar a mao.
 *
 * GIF passa INTACTO
 * =================
 * Desenhar um GIF num `<canvas>` captura UM frame e descarta a animacao. Como o
 * pedido foi explicitamente "foto/avatar/gif", perder a animacao seria perder o
 * que a pessoa quis. GIF grande e recusado pelo servidor com uma mensagem que
 * diz o que fazer, e isso e melhor que entregar um GIF parado sem avisar.
 */
export async function redimensionarImagem(
  file: File,
  options: { maxSide: number; maxBytes?: number } = { maxSide: 512 },
): Promise<{ data: string; mime: string }> {
  const maxBytes = options.maxBytes ?? 512 * 1024;

  const asBase64 = async (blob: Blob): Promise<string> => {
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    /*
     * Conversao em BLOCOS, nao `String.fromCharCode(...bytes)`.
     *
     * O spread de um array de 500 mil elementos como argumentos estoura o limite
     * de argumentos da engine ("Maximum call stack size exceeded") -- e o limite
     * varia por navegador, o que produz um bug que aparece na maquina de uns e
     * nao na de outros.
     */
    let binary = '';
    const CHUNK = 8192;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    return btoa(binary);
  };

  // GIF e SVG nao passam pelo canvas. SVG e recusado pelo servidor de qualquer
  // forma (pode conter script); mandar o original faz a mensagem de erro ser a
  // correta em vez de "formato desconhecido".
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return { data: await asBase64(file), mime: file.type };
  }

  const bitmap = await createImageBitmap(file);

  try {
    const scale = Math.min(1, options.maxSide / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D indisponivel neste navegador.');

    // `high` importa: o padrao do Chrome ao reduzir muito produz serrilhado
    // visivel num icone de 48px, que e o tamanho em que ele mais aparece.
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, width, height);

    /*
     * Tenta WebP com qualidade decrescente ate caber.
     *
     * WebP com qualidade 0.9 costuma sair 3 a 5 vezes menor que PNG sem
     * diferenca perceptivel num icone. O laco existe porque uma foto com muito
     * detalhe pode nao caber na primeira tentativa, e reduzir qualidade e
     * melhor que reduzir dimensao: o icone continua nitido no tamanho em que e
     * exibido.
     */
    for (const quality of [0.9, 0.75, 0.6, 0.45]) {
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/webp', quality);
      });
      if (blob && blob.size <= maxBytes) {
        return { data: await asBase64(blob), mime: 'image/webp' };
      }
    }

    throw new Error(
      'Nao foi possivel comprimir a imagem o suficiente. Use uma imagem menor.',
    );
  } finally {
    // `close()` libera a memoria do decodificador na hora. Sem isso, uma foto
    // de 12 megapixels fica pendurada ate o coletor decidir agir.
    bitmap.close();
  }
}

export type ConcordApi = ReturnType<typeof createConcordApi>;
